import axios from 'axios'

import { consumeSseStream } from './agent/stream'

// Backoff so tabs don't stampede the backend in lockstep on failure: 3s→60s, full jitter.
const RECONNECT_BASE_MS = 3000
const RECONNECT_MAX_MS = 60_000

/** One event on a per-user channel: `{type, data}`. A `reconnect` type is handled here and never delivered. */
export interface SseChannelEvent {
  type: string
  data?: unknown
}

// The session cookie authenticates the stream; this only asks for SSE.
function streamingHeaders(): Record<string, string> {
  return { Accept: 'text/event-stream, */*' }
}

export interface SseChannelOptions {
  /** Keeps the stream open while the tab is hidden, as for news a desktop notification announces. Read on each hide. */
  whileHidden?: () => boolean
}

/**
 * Follow a per-user SSE channel while the tab is visible, reconnecting on drop. Returns a function that stops it.
 * A hidden tab can't show realtime state, so its stream closes until the tab is visible again, unless whileHidden
 * keeps it.
 */
export function followSseChannel(
  url: string,
  onEvent: (event: SseChannelEvent) => void,
  { whileHidden = () => false }: SseChannelOptions = {}
): () => void {
  let closed = false
  let controller: AbortController | null = null
  let attempt = 0
  let wakeVisible: (() => void) | null = null
  let wakeSleep: (() => void) | null = null

  const parked = () => document.visibilityState === 'hidden' && !whileHidden()

  const waitForVisible = () =>
    new Promise<void>((resolve) => {
      if (!parked()) {
        resolve()
        return
      }
      wakeVisible = resolve
    })

  // wakeSleep() resolves this early so a stale backoff can't delay reconnecting once visible.
  const abortableSleep = (ms: number) =>
    new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        wakeSleep = null
        resolve()
      }, ms)
      wakeSleep = () => {
        clearTimeout(timer)
        wakeSleep = null
        resolve()
      }
    })

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      if (parked()) controller?.abort()
    } else {
      attempt = 0
      wakeVisible?.()
      wakeVisible = null
      wakeSleep?.()
    }
  }
  document.addEventListener('visibilitychange', onVisibilityChange)

  async function connect() {
    while (!closed) {
      await waitForVisible()
      if (closed) return
      controller = new AbortController()
      let serverReconnect = false
      try {
        const res = await axios.get(url, {
          headers: streamingHeaders(),
          responseType: 'stream',
          adapter: 'fetch',
          signal: controller.signal,
        })
        attempt = 0
        let reconnect = false
        await consumeSseStream(
          res.data as ReadableStream<Uint8Array>,
          (frame) => {
            if (typeof frame.data !== 'object' || frame.data === null) return
            const event = frame.data as { type?: string; data?: unknown }
            if (event.type === 'reconnect') {
              reconnect = true
              return true
            }
            if (event.type) onEvent({ type: event.type, data: event.data })
          },
          controller.signal
        )
        serverReconnect = reconnect
      } catch {
        if (closed) return
      }
      if (closed) return
      // Server-initiated recycle is planned, not a failure — reconnect now.
      if (serverReconnect) continue
      // Aborted by hide: loop back and park in waitForVisible, no backoff.
      if (parked()) continue
      const backoff = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS)
      attempt += 1
      await abortableSleep(backoff * (0.5 + Math.random() / 2))
    }
  }
  void connect()
  return () => {
    closed = true
    document.removeEventListener('visibilitychange', onVisibilityChange)
    controller?.abort()
    wakeVisible?.()
    wakeSleep?.()
  }
}
